import { useCallback, useEffect, useMemo, useState } from "react";

import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useServiceOrders } from "shared/hooks/useServiceOrders";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { formatDateTime } from "shared/lib/dateTime";
import { cancelInvoice, generateInvoiceAct } from "services/serviceOrders/rest";
import {
    InvoiceServiceOrderDto,
    PriceDto,
    ServiceOrderDto,
} from "services/serviceOrders/restModels";
import { formatTariffWithPeriod, parseTariffVariant } from "modules/settings/models";
import { useNotificationSnackbar } from "shared/ui";

import { PAGE_SIZE } from "features/PaymentsHistory/constants";
import { PaymentsHistoryState } from "features/PaymentsHistory/types";

export const usePaymentsHistoryState = (): PaymentsHistoryState => {
    const locale = useLocale();

    const notify = useNotificationSnackbar();

    const [page, setPage] = useState<number>(1);
    const [generatingActInvoiceId, setGeneratingActInvoiceId] = useState<string | null>(null);
    const [invoiceToCancel, setInvoiceToCancel] = useState<InvoiceServiceOrderDto | null>(null);
    const [cancelling, setCancelling] = useState<boolean>(false);

    const { orders, total, loading, error, refetch } = useServiceOrders(
        (page - 1) * PAGE_SIZE,
        PAGE_SIZE
    );
    const {
        constants,
        loading: constantsLoading,
        error: constantsError,
    } = useConstants();

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // Последняя оплата на странице могла быть отменена — уезжаем на страницу назад,
    // иначе пользователь остался бы на пустой.
    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    const currencies = useMemo(() => constants?.currencies || [], [constants]);

    const formatPrice = useCallback(
        (price: PriceDto) => {
            const currency = currencies.find((item) => item.id === price.currencyId) || null;

            return createCurrencyFormatter(currency, locale).toShow(price.price);
        },
        [currencies, locale]
    );

    /*
    * У счёта дата выставления информативнее даты оплаты: по ней заведение ищет документ
    * в бухгалтерии. У карты, наоборот, важен момент списания.
    * */
    const orderDate = useCallback((order: ServiceOrderDto) => {
        if (order.type === "Card" && order.status === "Paid" && order.paidAt) {
            return formatDateTime(order.paidAt);
        }

        return formatDateTime(order.createdAt);
    }, []);

    const tariffTitle = useCallback(
        (order: ServiceOrderDto) =>
            formatTariffWithPeriod(parseTariffVariant(order.tariff) || order.tariff, order.period),
        []
    );

    const goToPage = useCallback(
        (nextPage: number) => setPage(Math.min(Math.max(1, nextPage), pageCount)),
        [pageCount]
    );

    const openAct = useCallback(
        async (order: InvoiceServiceOrderDto) => {
            if (order.actLink) {
                window.open(order.actLink, "_blank");
                return;
            }

            setGeneratingActInvoiceId(order.invoiceId);
            try {
                const updated = await generateInvoiceAct(order.invoiceId);

                if (updated?.actLink) {
                    window.open(updated.actLink, "_blank");
                    refetch();
                } else {
                    notify.showError("Не удалось сформировать акт");
                }
            } catch (e) {
                notify.showError("Не удалось сформировать акт", e);
            } finally {
                setGeneratingActInvoiceId(null);
            }
        },
        [notify, refetch]
    );

    const openCancelDialog = useCallback(
        (order: InvoiceServiceOrderDto) => setInvoiceToCancel(order),
        []
    );

    const closeCancelDialog = useCallback(() => setInvoiceToCancel(null), []);

    const confirmCancel = useCallback(async () => {
        if (!invoiceToCancel) return;

        setCancelling(true);
        try {
            await cancelInvoice(invoiceToCancel.invoiceId);

            setInvoiceToCancel(null);
            // Отменённый счёт выпадает из истории — перечитываем страницу целиком.
            refetch();
            notify.showSuccess("Счёт отменён");
        } catch (e) {
            notify.showError("Не удалось отменить счёт", e);
        } finally {
            setCancelling(false);
        }
    }, [invoiceToCancel, notify, refetch]);

    return {
        orders,
        loading: loading || constantsLoading,
        error: error || constantsError,

        page,
        pageSize: PAGE_SIZE,
        total,
        goToPage,

        formatPrice,
        orderDate,
        tariffTitle,

        generatingActInvoiceId,
        openAct,

        invoiceToCancel,
        cancelling,
        openCancelDialog,
        closeCancelDialog,
        confirmCancel,

        notify,
    };
};
